"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

/**
 * P2-01 · The sticky <header> element with a condensed-on-scroll state.
 *
 * A 1px sentinel rendered just above the header leaves the viewport exactly
 * when the page scrolls past the top, flipping `data-condensed` (an
 * IntersectionObserver — no scroll listener). The header's layout height is
 * constant per breakpoint; condensing only changes paint (shadow/border) and
 * transforms, so the acceptance "no CLS on the header" holds by construction.
 * Contents are server-rendered and passed through as children.
 */
export function HeaderShell({ children }: { children: React.ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [condensed, setCondensed] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCondensed(entry ? !entry.isIntersecting : false),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      <header
        data-condensed={condensed || undefined}
        className={cn(
          // `group` lets children style their own condensed state (e.g. the
          // logo scales down) with transforms only — height stays constant.
          "group sticky top-0 z-40 -mt-px border-b bg-surface transition-shadow",
          condensed
            ? "border-border shadow-[0_4px_12px_-8px_rgb(0_0_0/0.35)]"
            : "border-transparent",
        )}
      >
        {children}
      </header>
    </>
  );
}
